import {
  Component,
  OnInit,
  OnDestroy,
  AfterViewInit,
  NgZone,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { Subscription, interval, Subject } from 'rxjs';
import { startWith, takeUntil } from 'rxjs/operators';
import { ApiCallsService } from '../../../shared/services/api-calls.service';
import { EvcRechargeHistoryComponent } from './components/evc-recharge-history.component';
import { EvcChargingHistoryComponent } from './components/evc-charging-history.component';
import { EvcDailyConsumptionComponent } from './components/evc-daily-consumption.component';
import { EvcMonthlyConsumptionComponent } from './components/evc-monthly-consumption.component';

// ─── Charger data shape ──────────────────────────────────────────────────────
export interface ChargerInfo {
  evName: string;                     // EV Name
  unitNo: string;                     // Unit No (formerly location no)
  parentLocationNo: string;           // EV Parent Unit No
  meterSerialNo: string;              // EV Sensor Serial No
  cbSerialNo: string;                 // EV CB Serial No
  gatewaySerialNo: string;            // EV Gateway Serial No
  dataLoggerId: string;               // EV Data Logger ID (GW tag)
  dicName: string;                    // EV DIC / Gateway Name
  siteName: string;
  siteCode: string;
  state: 'Restored' | 'Cut';
  paymentType: 'Prepaid' | 'Postpaid';// Y = Prepaid, N = Postpaid
  lastPowerCutTime: string;           // "YYYY-MM-DD HH:mm:ss"
  balanceAmount: number;              // EV Balance Amount
  lowBalanceThreshold: number;
  isLowBalanceAlert: boolean;         // EV Low Balance Alert
  isActiveCutOff: boolean;            // EV Active Cut Off
  meterReadingReceived: string;
  dicReadingReceived: string;
  meterStatus: 'Y' | 'N';
  dicStatus: 'Y' | 'N';
  mobileNo: string;
  supervisorName: string;
  supervisorContact: string;

  // Phase Wise Voltage & Current
  voltageR: number;                   // VR (V)
  voltageY: number;                   // VY (V)
  voltageB: number;                   // VB (V)
  currentR: number;                   // IR (A)
  currentY: number;                   // IY (A)
  currentB: number;                   // IB (A)

  // Power & Load
  powerFactor: number;                // PF
  instantLoadKw: number;              // Instant Load kW
  instantLoadKva: number;             // Instant Load kVA
}

@Component({
  selector: 'app-ev-charger',
  standalone: true,
  imports: [
    CommonModule,
    EvcRechargeHistoryComponent,
    EvcChargingHistoryComponent,
    EvcDailyConsumptionComponent,
    EvcMonthlyConsumptionComponent
  ],
  templateUrl: './ev-charger.component.html',
  styleUrl: './ev-charger.component.scss',
})
export class EvChargerComponent implements OnInit, AfterViewInit, OnDestroy {

  // ── ID Variables (from incoming JSON payload) ──────────────────────────────
  EV_DATA: any = null;
  evData: any = null;
  sensorId: string = '';
  siteId: string = '';
  serialNo: string = '';
  evLocationId: string = '';
  mainMeterLocationId: string = '';
  emailId: string = '';
  mobileNo: string = '';
  evLocationName: string = '';
  evMeterName: string = '';
  mainMeterLocationName: string = '';
  chargingState: string = 'N';

  districtId: string = '';
  locationList: any[] = [];

  // ── Charger hero data ───────────────────────────────────────────────────────
  charger: ChargerInfo | null = null;
  chargerLoading = true;
  durationLabel = '—';
  private tickerSub?: Subscription;
  private destroy$ = new Subject<void>();

  // ── Local UI testing ───────────────────────────────────────────────
  /**
   * LOCAL UI TEST SWITCH
   * true  = force charger into enabled / charging state
   * false = use API state (or cut if disconnected)
   */
  localChargingOverride = false;

  readonly Math = Math;

  get isCharging(): boolean {
    if (this.localChargingOverride) return true;
    return this.chargingState === 'Y' || this.charger?.state === 'Restored';
  }

  // ── RYB Donut & Circular Gauge Math ─────────────────────────────────────────
  get currentR(): number {
    return this.charger?.currentR ?? 0;
  }

  get currentY(): number {
    return this.charger?.currentY ?? 0;
  }

  get currentB(): number {
    return this.charger?.currentB ?? 0;
  }

  get totalCurrent(): number {
    const total = this.currentR + this.currentY + this.currentB;
    return Math.round(total * 100) / 100;
  }

  // Donut geometry (radius = 50, C = 2 * PI * 50 = ~314.159)
  readonly donutRadius = 50;
  readonly donutCircumference = 2 * Math.PI * 50;

  get rArcDash(): string {
    if (this.totalCurrent <= 0) return `0 ${this.donutCircumference}`;
    const len = (this.currentR / this.totalCurrent) * this.donutCircumference;
    return `${len} ${this.donutCircumference}`;
  }

  get rArcOffset(): number {
    return 0;
  }

  get yArcDash(): string {
    if (this.totalCurrent <= 0) return `0 ${this.donutCircumference}`;
    const len = (this.currentY / this.totalCurrent) * this.donutCircumference;
    return `${len} ${this.donutCircumference}`;
  }

  get yArcOffset(): number {
    if (this.totalCurrent <= 0) return 0;
    const offset = (this.currentR / this.totalCurrent) * this.donutCircumference;
    return -offset;
  }

  get bArcDash(): string {
    if (this.totalCurrent <= 0) return `0 ${this.donutCircumference}`;
    const len = (this.currentB / this.totalCurrent) * this.donutCircumference;
    return `${len} ${this.donutCircumference}`;
  }

  get bArcOffset(): number {
    if (this.totalCurrent <= 0) return 0;
    const offset = ((this.currentR + this.currentY) / this.totalCurrent) * this.donutCircumference;
    return -offset;
  }

  // Individual phase radial dials (radius = 38, C = 2 * PI * 38 = ~238.761)
  readonly dialRadius = 38;
  readonly dialCircumference = 2 * Math.PI * 38;
  readonly maxPhaseCurrent = 32; // Standard 32A rating

  get rDialDash(): string {
    const pct = Math.min(1, Math.max(0, this.currentR / this.maxPhaseCurrent));
    return `${pct * this.dialCircumference} ${this.dialCircumference}`;
  }

  get yDialDash(): string {
    const pct = Math.min(1, Math.max(0, this.currentY / this.maxPhaseCurrent));
    return `${pct * this.dialCircumference} ${this.dialCircumference}`;
  }

  get bDialDash(): string {
    const pct = Math.min(1, Math.max(0, this.currentB / this.maxPhaseCurrent));
    return `${pct * this.dialCircumference} ${this.dialCircumference}`;
  }

  get phaseBalanceStatus(): { label: string; class: string } {
    if (this.totalCurrent === 0) {
      return { label: 'Standby / 0.0 A', class: 'status-standby' };
    }
    const max = Math.max(this.currentR, this.currentY, this.currentB);
    const min = Math.min(this.currentR, this.currentY, this.currentB);
    const diff = max - min;
    if (diff < 1.5) {
      return { label: 'Balanced Load', class: 'status-balanced' };
    } else if (diff < 4) {
      return { label: 'Slight Imbalance', class: 'status-warn' };
    } else {
      return { label: 'Phase Imbalance', class: 'status-unbalanced' };
    }
  }

  constructor(
    private route: ActivatedRoute,
    private api: ApiCallsService,
    private zone: NgZone,
  ) { }

  // ── Lifecycle ───────────────────────────────────────────────────────────────

  ngOnInit(): void {
    // 1. Resolve immediately on  ngOnInit
    this.resolveSiteAndLocation();
    this.loadCharger();
    this.getLocations();
    // 2. React to dynamic query param changes
    this.route.queryParamMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
      const locEnc = params.get('user') || params.get('location_id') || params.get('id') || params.get('loc_id') || params.get('location') || '';
      const siteEnc = params.get('site_id') || params.get('siteid') || params.get('site') || params.get('site_ids') || '';
      let changed = false;

      if (locEnc) {
        let decoded = '';
        try { decoded = atob(locEnc); } catch { decoded = locEnc; }
        if (decoded.includes('__')) {
          const [loc, site] = decoded.split('__');
          if (loc && this.evLocationId !== loc) {
            this.evLocationId = loc;
            changed = true;
          }
          if (site && this.siteId !== site) {
            this.siteId = site;
            localStorage.setItem('selected_site_id', site);
            sessionStorage.setItem('consumerSiteId', site);
            changed = true;
          }
        } else if (this.evLocationId !== decoded) {
          this.evLocationId = decoded;
          changed = true;
        }
      }
      if (siteEnc) {
        let decodedSite = '';
        try { decodedSite = atob(siteEnc); } catch { decodedSite = siteEnc; }
        if (decodedSite && this.siteId !== decodedSite) {
          this.siteId = decodedSite;
          localStorage.setItem('selected_site_id', decodedSite);
          sessionStorage.setItem('consumerSiteId', decodedSite);
          changed = true;
        }
      }

      if (changed) {
        this.loadCharger();
        this.getLocations();
      }
    });

    // Live tick: update duration label every 60 s
    this.tickerSub = interval(60_000).pipe(startWith(0)).subscribe(() => {
      if (this.charger) {
        this.durationLabel = this.buildDurationLabel(this.charger);
      }
    });
  }

  private mapParentLocation(): void {
    if (!this.locationList || this.locationList.length === 0) return;

    let parentLoc: any = null;

    // 1. Match district_id from evdetail response with id in location list
    if (this.districtId && this.districtId !== '--') {
      parentLoc = this.locationList.find(
        (loc: any) => String(loc.id ?? loc.locationId ?? loc.location_id ?? '') === String(this.districtId)
      );
    }

    // 2. Fallback: find current location in list and check if district_id is present on it
    if (!parentLoc && this.evLocationId && this.evLocationId !== '--') {
      const currentLoc = this.locationList.find(
        (loc: any) => String(loc.locationId ?? loc.location_id ?? loc.id ?? '') === String(this.evLocationId)
      );
      if (currentLoc) {
        const dId = currentLoc.district_id ?? currentLoc.districtId ?? currentLoc.district;
        if (dId) {
          this.districtId = String(dId);
          parentLoc = this.locationList.find(
            (loc: any) => String(loc.id ?? loc.locationId ?? loc.location_id ?? '') === String(dId)
          );
        }
      }
    }

    if (parentLoc) {
      const parentLocationNo = parentLoc.location_no ?? parentLoc.locationNo ?? parentLoc.location_name ?? parentLoc.locationName ?? '';
      if (parentLocationNo) {
        this.mainMeterLocationName = parentLocationNo;
        if (this.charger) {
          this.charger.parentLocationNo = parentLocationNo;
        }
      }
    }
  }

  private getLocations(): void {
    if (!this.siteId || this.siteId === '--') return;

    this.api.getLocationList(this.siteId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res: any) => {
        const list: any[] = res?.Data ?? res?.locationMaster ?? res?.data ?? (Array.isArray(res) ? res : []);
        this.locationList = Array.isArray(list) ? list : [];
        console.log('Location list fetched in EvChargerComponent:', this.locationList);

        this.mapParentLocation();
      },
      error: (err) => {
        console.error('Error fetching location list in EvChargerComponent', err);
      }
    });
  }

  private resolveSiteAndLocation(): void {
    // 0. Load stored EV_DATA if available
    try {
      const stored = localStorage.getItem('EV_DATA') || sessionStorage.getItem('EV_DATA');
      if (stored) {
        this.EV_DATA = JSON.parse(stored);
        this.evData = this.EV_DATA;
        console.log('EV_DATA loaded in EV Charger component:', this.EV_DATA);
      }
    } catch (e) {
      console.error('Error loading EV_DATA in EvChargerComponent', e);
    }

    try {
      const cachedLocs = localStorage.getItem('locationList');
      if (cachedLocs) {
        const parsed = JSON.parse(cachedLocs);
        if (Array.isArray(parsed)) {
          this.locationList = parsed;
        }
      }
    } catch { }

    // 1. Check Query Params (snapshot & decoded)
    const qp = this.route.snapshot.queryParamMap;
    const locParam = qp.get('user') || qp.get('location_id') || qp.get('id') || qp.get('loc_id') || qp.get('location') || '';
    const siteParam = qp.get('site_id') || qp.get('siteid') || qp.get('site') || qp.get('site_ids') || '';

    let resolvedLoc = '';
    let resolvedSite = '';

    if (locParam) {
      let decoded = '';
      try { decoded = atob(locParam); } catch { decoded = locParam; }
      if (decoded.includes('__')) {
        const parts = decoded.split('__');
        resolvedLoc = parts[0];
        resolvedSite = parts[1];
      } else {
        resolvedLoc = decoded;
      }
    }

    if (!resolvedSite && siteParam) {
      try { resolvedSite = atob(siteParam); } catch { resolvedSite = siteParam; }
    }

    // Fallbacks from EV_DATA if query params not provided
    if (!resolvedLoc && this.EV_DATA) {
      resolvedLoc = String(this.EV_DATA.ev_location_id ?? this.EV_DATA.location_id ?? this.EV_DATA.location_no ?? '');
    }
    if (!resolvedSite && this.EV_DATA) {
      resolvedSite = String(this.EV_DATA.site_id ?? '');
    }

    // 2. Fallbacks from Storage
    if (!resolvedLoc) {
      resolvedLoc = localStorage.getItem('login_id') ||
        localStorage.getItem('location_id') ||
        localStorage.getItem('offline_Location') ||
        '9440991006';
    }

    if (!resolvedSite) {
      resolvedSite = sessionStorage.getItem('consumerSiteId') ||
        localStorage.getItem('selected_site_id') ||
        localStorage.getItem('site_idLogin') ||
        localStorage.getItem('site_id') || '';

      if (!resolvedSite) {
        try {
          const siteList = JSON.parse(localStorage.getItem('siteList') || '[]');
          if (Array.isArray(siteList) && siteList.length > 0 && siteList[0].site_id) {
            resolvedSite = String(siteList[0].site_id);
          }
        } catch { }
      }

      if (!resolvedSite) {
        resolvedSite = '59.64062087';
      }
    }

    if (resolvedSite) {
      localStorage.setItem('selected_site_id', resolvedSite);
      sessionStorage.setItem('consumerSiteId', resolvedSite);
    }

    this.evLocationId = resolvedLoc;
    this.siteId = resolvedSite;
  }

  ngAfterViewInit(): void { }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.tickerSub?.unsubscribe();
  }

  // ── Charger info loader ─────────────────────────────────────────────────────

  loadCharger(): void {
    this.chargerLoading = true;

    // Apply EV_DATA immediately from storage if already present
    if (this.EV_DATA) {
      this.applyPayloadData(this.EV_DATA);
      this.durationLabel = this.buildDurationLabel(this.charger!);
    }

    this.api.getEvBasicDetails(this.siteId, this.evLocationId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        const data = res?.Data ?? res?.data ?? res;
        let item: any = null;
        if (Array.isArray(data?.list) && data.list.length > 0) {
          item = data.list.find((i: any) =>
            String(i.ev_location_id ?? i.location_id ?? '') === this.evLocationId ||
            String(i.sensor_id ?? '') === this.sensorId ||
            String(i.serial_no ?? i.meter_sr_no ?? '') === this.serialNo
          ) ?? data.list[0];
        } else if (Array.isArray(data) && data.length > 0) {
          item = data[0];
        } else if (data && typeof data === 'object' && Object.keys(data).length > 0) {
          item = data;
        }

        if (item) {
          this.applyPayloadData(item);
        } else if (this.EV_DATA) {
          this.applyPayloadData(this.EV_DATA);
        } else {
          this.applySampleFallback();
        }
        this.durationLabel = this.buildDurationLabel(this.charger!);
        this.chargerLoading = false;
      },
      error: (err) => {
        console.error('Error fetching EvBasicDetails', err);
        if (this.EV_DATA) {
          this.applyPayloadData(this.EV_DATA);
        } else {
          this.applySampleFallback();
        }
        this.durationLabel = this.buildDurationLabel(this.charger!);
        this.chargerLoading = false;
      },
    });
  }

  private applyPayloadData(item: any): void {
    let storedData: any = {};
    try {
      const rawStored = localStorage.getItem('EV_DATA') || sessionStorage.getItem('EV_DATA');
      if (rawStored) storedData = JSON.parse(rawStored);
    } catch { }

    const merged = { ...storedData, ...item };

    // ── Save all incoming IDs and fields into variables ───────────────────────
    this.sensorId = String(merged.sensor_id ?? merged.sensorId ?? this.sensorId ?? '');
    this.siteId = String(merged.site_id ?? merged.siteId ?? this.siteId ?? '');
    this.serialNo = String(merged.serial_no ?? merged.meter_sr_no ?? merged.sensor_sr_no ?? merged.meterSerialNo ?? this.serialNo ?? '');
    this.evLocationId = String(merged.ev_location_id ?? merged.location_id ?? merged.loc_id ?? this.evLocationId ?? '');
    this.mainMeterLocationId = String(merged.main_meter_location_id ?? merged.parent_location_id ?? merged.parentLocationId ?? this.mainMeterLocationId ?? '');
    this.emailId = String(merged.email_id ?? merged.email ?? this.emailId ?? '');
    this.mobileNo = String(merged.mobile_no ?? merged.mobile ?? merged.mobileNo ?? this.mobileNo ?? '');
    this.evLocationName = String(merged.ev_location_name ?? merged.location_name ?? merged.location_no ?? merged.locationNo ?? this.evLocationName ?? '');
    this.evMeterName = String(merged.ev_meter_name ?? merged.ev_name ?? merged.meter_name ?? merged.evName ?? this.evMeterName ?? '');
    this.mainMeterLocationName = String(merged.main_meter_location_name ?? merged.parent_location_name ?? merged.parent_location_no ?? merged.parentLocationNo ?? this.mainMeterLocationName ?? '');
    this.chargingState = String(merged.charging_state ?? (merged.state === 'Restored' ? 'Y' : (merged.state === 'Cut' ? 'N' : this.chargingState ?? 'N')));

    let resolvedSiteName = merged.site_name ?? '';
    let resolvedSiteCode = merged.short_code ?? merged.site_code ?? '';
    if (!resolvedSiteName || !resolvedSiteCode) {
      try {
        const siteList = JSON.parse(localStorage.getItem('siteList') || '[]');
        const matchedSite = siteList.find((s: any) => String(s.site_id) === String(this.siteId));
        if (matchedSite) {
          if (!resolvedSiteName) resolvedSiteName = matchedSite.site_name || '';
          if (!resolvedSiteCode) resolvedSiteCode = matchedSite.short_code || '';
        }
      } catch { }
    }

    const rCur = parseFloat(merged.R_Current ?? merged.current_r ?? merged.currentR ?? (this.localChargingOverride ? 15.2 : 0.0));
    const yCur = parseFloat(merged.Y_Current ?? merged.current_y ?? merged.currentY ?? (this.localChargingOverride ? 14.8 : 0.0));
    const bCur = parseFloat(merged.B_Current ?? merged.current_b ?? merged.currentB ?? (this.localChargingOverride ? 15.6 : 0.0));
    const bal = parseFloat(merged.balance_amt ?? merged.balanceAmount ?? merged.balance ?? 1795.04);
    const isCut = this.chargingState === 'N' && !this.localChargingOverride;

    const rawActiveCutOff = String(merged.active_cut_off ?? merged.activeCutOff ?? 'N').trim().toUpperCase();
    const paymentType: 'Prepaid' | 'Postpaid' = rawActiveCutOff === 'Y' ? 'Prepaid' : 'Postpaid';
    const dataLoggerId = String(merged.data_logger_id ?? merged.dataLoggerId ?? merged.gateway_sr_no ?? merged.gateway_serial_no ?? '—');
    const dicName = String(merged.dic_name ?? merged.dicName ?? merged.gateway_name ?? '—');

    this.districtId = String(merged.district_id ?? merged.districtId ?? this.districtId ?? '');
    this.mapParentLocation();

    this.charger = {
      evName: this.evMeterName || this.evLocationName || merged.consumer_name || 'EV Fast Charger',
      unitNo: this.evLocationName || this.evLocationId || '---',
      parentLocationNo: this.mainMeterLocationName || '---',
      meterSerialNo: this.serialNo || '1617503',
      cbSerialNo: merged.cb_sr_no ?? merged.cb_serial_no ?? 'CB-EV-9921',
      gatewaySerialNo: dataLoggerId,
      dataLoggerId: dataLoggerId,
      dicName: dicName,
      siteName: resolvedSiteName || localStorage.getItem('site_name') || 'Mobility Hub Station',
      siteCode: resolvedSiteCode || 'HUB-01',
      state: isCut ? 'Cut' : 'Restored',
      paymentType: paymentType,
      lastPowerCutTime: merged.last_power_cut_time ?? merged.power_cut_time ?? new Date(Date.now() - 2 * 3600_000).toISOString().replace('T', ' ').substring(0, 19),
      balanceAmount: bal,
      lowBalanceThreshold: parseFloat(merged.low_balance_threshold ?? merged.lowBalanceThreshold ?? 200),
      isLowBalanceAlert: bal < parseFloat(merged.low_balance_threshold ?? 200),
      isActiveCutOff: isCut,
      meterReadingReceived: merged.meter_reading_received ?? merged.last_reading_time ?? new Date(Date.now() - 3 * 60_000).toLocaleTimeString('en-IN'),
      dicReadingReceived: merged.dic_reading_received ?? new Date(Date.now() - 5 * 60_000).toLocaleTimeString('en-IN'),
      meterStatus: merged.meter_status === 'Y' ? 'Y' : 'N',
      dicStatus: merged.dic_status === 'Y' ? 'Y' : 'N',
      mobileNo: this.mobileNo || '+91 98107 05004',
      supervisorName: merged.supervisor_name ?? 'Suresh Kumar',
      supervisorContact: merged.supervisor_contact_no ?? merged.supervisor_mobile ?? '+91 91234 56789',

      // Phase wise electrical
      voltageR: parseFloat(merged.voltage_r ?? merged.voltageR ?? merged.R_Voltage ?? (this.isCharging ? 232.4 : 0)),
      voltageY: parseFloat(merged.voltage_y ?? merged.voltageY ?? merged.Y_Voltage ?? (this.isCharging ? 230.1 : 0)),
      voltageB: parseFloat(merged.voltage_b ?? merged.voltageB ?? merged.B_Voltage ?? (this.isCharging ? 231.8 : 0)),
      currentR: rCur,
      currentY: yCur,
      currentB: bCur,

      // Power & Load
      instantLoadKw: parseFloat(merged.instant_cum_KW ?? merged.instant_kw ?? 0),
      instantLoadKva: parseFloat(merged.instant_cum_KVA ?? merged.instant_kva ?? 0),
      powerFactor: parseFloat(merged.instant_cum_KVA) > 0 ? (parseFloat(merged.instant_cum_KW ?? 0) / parseFloat(merged.instant_cum_KVA ?? 0)) : 0,
    };
  }

  private applySampleFallback(): void {
    // Sample JSON structure as specified by user
    const sample = {
      sensor_id: '--',
      site_id: '--',
      serial_no: '--',
      ev_meter_name: '--',
      ev_location_id: '--',
      charging_state: this.localChargingOverride ? 'Y' : 'N',
      active_cut_off: 'N',
      dic_name: '--',
      data_logger_id: '--',
      R_Current: this.localChargingOverride ? '15.20' : '0.00',
      Y_Current: this.localChargingOverride ? '14.80' : '0.00',
      B_Current: this.localChargingOverride ? '15.60' : '0.00',
      ev_location_name: '--',
      mobile_no: '--',
      email_id: '--',
      district_id: '--',
      main_meter_location_id: '--',
      main_meter_location_name: '--'
    };
    this.applyPayloadData(sample);
  }

  // ── Duration helpers ────────────────────────────────────────────────────────

  buildDurationLabel(c: ChargerInfo): string {
    if (!this.isCharging) return '—';
    return this.getChargingDuration(c.lastPowerCutTime);
  }

  getChargingDuration(ts: string): string {
    if (!ts || ts.startsWith('0000')) return '—';
    const [datePart, timePart] = ts.split(' ');
    const [y, mo, d] = datePart.split('-').map(Number);
    const [h, mi, s] = (timePart ?? '00:00:00').split(':').map(Number);
    const since = new Date(y, mo - 1, d, h, mi, s);
    const totalMins = Math.max(0, Math.floor((Date.now() - since.getTime()) / 60000));
    const days = Math.floor(totalMins / 1440);
    const hours = Math.floor((totalMins % 1440) / 60);
    const mins = totalMins % 60;
    const parts: string[] = [];
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    parts.push(`${mins}m`);
    return parts.join(' ');
  }

  // ── Balance class ───────────────────────────────────────────────────────────

  getBalanceClass(amt: number): string {
    if (amt < 0) return 'balance-neg';
    if (amt < 200) return 'balance-warn';
    return 'balance-pos';
  }

  // ── Reading age chip class ──────────────────────────────────────────────────

  getMeterAgeClass(): string {
    if (!this.charger) return 'age-chip age-fresh';
    const flag = this.charger.meterStatus;
    return flag === 'Y' ? 'age-chip age-critical' : 'age-chip age-fresh';
  }

  getMeterAgeLabel(): string {
    if (!this.charger) return '—';
    return this.charger.meterStatus === 'Y' ? 'Stale' : 'Normal';
  }
}
